import React from "react";
import styled from "styled-components";

import Arc from "../components/shapes/Arc";
import ArcHollow from "../components/shapes/ArcHollow";
import {
  computeSectorCentroid,
  computeArcCentroid,
  toProperCase,
} from "../utils/helpers";
import {
  eightCogFuncs,
  domAux,
  domAuxTerInf,
  cogFuncPriorities,
} from "../utils/globalConst";
// import { cogFuncAllConfig } from "../data/cogFuncAllConfig";
import {
  ClipPathStyled,
  ArcLabelExvert,
  ArcLabelInvert,
  StackLabelExvert,
  StackLabelInvert,
} from "./styledComponents";

const defaultStylesProps = {
  x: 250,
  y: 250,
  startAngle: -90,
  endAngle: 0,
  radiusFull: 250,
  gap: 0,
};

const CognitiveFunction = ({
  type = "ESTJ",
  cogFunc: currCogFunc,
  isExvert = true,
  stackFunc: currStackFunc,
  chartId = "",
  stylesProps = defaultStylesProps,
  config,
  hasTransition = true,
  showLabels = true,
  showShadowFunc,
  onArcHover,
}) => {
  const { cogFuncConfig, stackFuncConfig, xVersionConfig } = config;

  const cogFuncIdx = cogFuncConfig.map((d) => d.cogFunc).indexOf(currCogFunc);
  const isExvertIdx = isExvert ? 0 : 1;
  const stackFuncIdx = stackFuncConfig
    .map((d) => d.stackFunc)
    .indexOf(currStackFunc);

  const {
    style: { fill, quad },
  } = cogFuncConfig[cogFuncIdx];
  const {
    style: { rotation },
  } = stackFuncConfig[stackFuncIdx];
  const {
    style: { areaRatio, innerRadiusRatio },
  } = xVersionConfig[isExvertIdx];

  const commonProps = {
    ...stylesProps,
    isExvert,
    fill,
    hasTransition,
  };

  const { x, y, radiusFull } = commonProps;
  // const rScaleArea = (areaRatio) => radiusFull / Math.sqrt(areaRatio + 1);
  const rScaleInnerRad = (ir) => radiusFull * ir;

  const arcHollowProps = {
    ...commonProps,
    radius: radiusFull,
    radiusHollow: rScaleInnerRad(innerRadiusRatio),
  };
  const arcProps = {
    ...commonProps,
    radius: rScaleInnerRad(innerRadiusRatio),
  };

  const isExvertLabel = isExvert ? "extravert" : "introvert";
  const uniqueId = `${chartId}-${type}-${currCogFunc}-${currStackFunc}-${isExvertLabel}`;
  const cogFuncId = `cogfunc-${uniqueId}`;
  const clipPathId = `clip-path-${uniqueId}`;
  const transformClipPath = `rotate(${rotation})`;
  const transformCogFunc = `scale(${quad[0] ? -1 : 1} ${quad[1] ? -1 : 1})`;

  const extraversionLabel = `extraverted ${currCogFunc}`;
  const introversionLabel = `introverted ${currCogFunc}`;

  const getXvnLabelShort = (fn) =>
    eightCogFuncs[eightCogFuncs.map((d) => d.function).indexOf(fn)].short;

  const [xExtra, yExtra] = computeArcCentroid(
    rotation + 180,
    rScaleInnerRad(innerRadiusRatio),
    radiusFull
  );
  const [xIntro, yIntro] = computeSectorCentroid(
    rotation + 180,
    rScaleInnerRad(innerRadiusRatio)
  );

  const transformLabel = `translate(${quad[0] ? radiusFull : 0}, ${
    quad[1] ? radiusFull : 0
  })`;
  const xExtraLabel = quad[0] ? xExtra : radiusFull - xExtra;
  const yExtraLabel = quad[1] ? yExtra : radiusFull - yExtra;
  const xIntroLabel = quad[0] ? xIntro : radiusFull - xIntro;
  const yIntroLabel = quad[1] ? yIntro : radiusFull - yIntro;

  const labelProps = { currStackFunc, isExvert };

  const handleHover = (isHandleExvert) => {
    onArcHover({ currCogFunc, currStackFunc, isHandleExvert });
  };

  return (
    <g className="cognitive-function">
      <g
        className="cogfunc-arcs"
        transform={transformCogFunc}
        style={{ transformOrigin: "center" }}
      >
        <ClipPathStyled
          id={cogFuncId}
          transform={transformClipPath}
          hasTransition={hasTransition}
        >
          <rect id={clipPathId} width={x} height={y} />
        </ClipPathStyled>
        {/* <use xlinkHref={`#${clipPathId}`} fill="#eee" /> */}
        <g className="arcs" clipPath={`url(#${cogFuncId})`}>
          <ArcHollow
            {...arcHollowProps}
            handleHover={() => handleHover(true)}
          />
          <Arc {...arcProps} handleHover={() => handleHover(false)} />
        </g>
      </g>
      {showLabels && (
        <g
          className="labels"
          transform={transformLabel}
          style={{ pointerEvents: "none" }}
        >
          <g className="cogfunc-labels">
            <ArcLabelExvert
              className="arc-hollow-label"
              x={xExtraLabel}
              y={yExtraLabel}
              {...labelProps}
            >
              {toProperCase(getXvnLabelShort(extraversionLabel))}
            </ArcLabelExvert>
            <ArcLabelInvert
              className="arc-label"
              x={xIntroLabel}
              y={yIntroLabel}
              {...labelProps}
            >
              {toProperCase(getXvnLabelShort(introversionLabel))}
            </ArcLabelInvert>
          </g>
          <g className="stackfunc-labels">
            {isExvert ? (
              <StackLabelExvert
                className="stackfunc-exvert-label"
                x={xExtraLabel}
                y={yExtraLabel + 18}
                {...labelProps}
              >
                {currStackFunc.substring(0, 3)}
              </StackLabelExvert>
            ) : (
              <StackLabelInvert
                className="stackfunc-invert-label"
                x={xIntroLabel}
                y={yIntroLabel + 18}
                {...labelProps}
              >
                {currStackFunc.substring(0, 3)}
              </StackLabelInvert>
            )}
          </g>
        </g>
      )}
    </g>
  );
};

export default CognitiveFunction;
